import { useEffect, useState } from 'react'
import { useAuthStore } from '../modules/auth/store'
import { useNotificationsStore } from '../modules/notifications/store'
import { ResponsiveLayout } from '../shared/components/layout'
import { StatusCard, Badge } from '../shared/components/widgets'
import { api } from '../shared/lib/api'

export function ProfilePage() {
  const { user, logout } = useAuthStore()
  const {
    isCurrentBrowserSubscribed,
    subscriptions,
    currentBrowserEndpoint,
    permission,
    isLoading,
    error,
    checkStatus,
    subscribe,
    unsubscribe,
    unsubscribeDevice,
    clearError,
  } = useNotificationsStore()

  const [testStatus, setTestStatus] = useState<string | null>(null)

  useEffect(() => {
    checkStatus()
  }, [checkStatus])

  if (!user) return null

  const handleToggleNotifications = async () => {
    setTestStatus(null)

    if (isCurrentBrowserSubscribed) {
      try {
        await unsubscribe()
        setTestStatus('Navegador desregistrado')
        setTimeout(() => setTestStatus(null), 3000)
      } catch {
        // Error handled in store
      }
      return
    }

    // For setup: request permission and show success notification
    if (!('Notification' in window)) {
      setTestStatus('Tu navegador no soporta notificaciones')
      return
    }

    let perm = Notification.permission
    if (perm === 'default') {
      perm = await Notification.requestPermission()
    }

    if (perm !== 'granted') {
      setTestStatus('Permiso de notificaciones denegado')
      return
    }

    // Try to subscribe to push (may fail if VAPID not configured)
    try {
      await subscribe()
    } catch {
      // Push subscription failed, but browser notifications work
      // This is OK for local development
    }

    // Show success notification via service worker (required for mobile browsers)
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready
        await registration.showNotification('🐾 Rondela', {
          body: '¡Notificaciones activadas correctamente!',
          icon: '/icons/icon-192x192.png',
          tag: 'setup-notification',
        })
      }
      setTestStatus('¡Configuración exitosa!')
      setTimeout(() => setTestStatus(null), 3000)
    } catch (err) {
      setTestStatus(`Error: ${err instanceof Error ? err.message : 'Desconocido'}`)
    }
  }

  const handleTestPushNotification = async () => {
    setTestStatus(null)
    setTestStatus('Enviando notificación push...')

    try {
      await api.post('/api/notifications/test')
      setTestStatus('¡Push enviado! Debería llegar pronto...')
      setTimeout(() => setTestStatus(null), 5000)
    } catch (err) {
      setTestStatus(`Error: ${err instanceof Error ? err.message : 'Desconocido'}`)
    }
  }

  const handleRemoveDevice = async (subscriptionId: string) => {
    try {
      await unsubscribeDevice(subscriptionId)
      setTestStatus('Dispositivo eliminado')
      setTimeout(() => setTestStatus(null), 3000)
    } catch (err) {
      setTestStatus(`Error: ${err instanceof Error ? err.message : 'Desconocido'}`)
    }
  }

  return (
    <ResponsiveLayout>
      <div className="page">
        <div className="page-header">
          <h1>Mi perfil</h1>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
            <button onClick={clearError} className="alert-dismiss">&times;</button>
          </div>
        )}

        <div className="profile-cards">
          <StatusCard status="neutral">
            <StatusCard.Header>
              <StatusCard.Title>Información personal</StatusCard.Title>
            </StatusCard.Header>
            <StatusCard.Content collapsible={false}>
              <dl className="info-list">
                <div className="info-item">
                  <dt>Nombre</dt>
                  <dd>{user.nombre}</dd>
                </div>
                <div className="info-item">
                  <dt>Email</dt>
                  <dd>{user.email}</dd>
                </div>
                <div className="info-item">
                  <dt>Rol</dt>
                  <dd>
                    <Badge variant="status" status={user.role === 'admin' ? 'info' : 'neutral'}>
                      {user.role === 'admin' ? 'Administrador' : 'Cuidador'}
                    </Badge>
                  </dd>
                </div>
                <div className="info-item">
                  <dt>Grupo</dt>
                  <dd>{user.careGroupNombre}</dd>
                </div>
              </dl>
            </StatusCard.Content>
          </StatusCard>

          <StatusCard status="neutral">
            <StatusCard.Header>
              <StatusCard.Title>Notificaciones</StatusCard.Title>
            </StatusCard.Header>
            <StatusCard.Content collapsible={false}>
              <div className="notification-settings">
                {permission === 'denied' && (
                  <p className="text-warning" style={{ marginBottom: '1rem' }}>
                    Las notificaciones están bloqueadas en tu navegador.
                    Habilítalas desde la configuración del navegador.
                  </p>
                )}

                {/* Current browser status */}
                <div className="notification-status" style={{ marginBottom: '1rem' }}>
                  <p>
                    <strong>Este navegador:</strong>{' '}
                    {isCurrentBrowserSubscribed ? (
                      <span className="text-success">Registrado</span>
                    ) : (
                      <span className="text-muted">No registrado</span>
                    )}
                  </p>
                </div>

                <div className="notification-buttons" style={{ marginBottom: '1.5rem' }}>
                  {!isCurrentBrowserSubscribed ? (
                    <button
                      onClick={handleToggleNotifications}
                      className="btn btn-primary"
                      disabled={isLoading || permission === 'denied'}
                    >
                      {isLoading ? 'Procesando...' : 'Registrar este navegador'}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleToggleNotifications}
                        className="btn btn-secondary"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Procesando...' : 'Desregistrar este navegador'}
                      </button>
                      <button
                        onClick={handleTestPushNotification}
                        className="btn btn-ghost"
                        disabled={isLoading}
                        title="Enviar notificación push de prueba"
                      >
                        Probar Push
                      </button>
                    </>
                  )}
                </div>

                {testStatus && (
                  <p className={testStatus.includes('Error') || testStatus.includes('denegado') ? 'text-warning' : 'text-success'} style={{ marginBottom: '1rem' }}>
                    {testStatus}
                  </p>
                )}

                {/* Registered devices list */}
                {subscriptions.length > 0 && (
                  <div className="devices-list" style={{ marginTop: '1.5rem' }}>
                    <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>Dispositivos registrados ({subscriptions.length})</h3>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {subscriptions.map((sub) => {
                        const isCurrentDevice = sub.endpoint === currentBrowserEndpoint
                        return (
                          <li
                            key={sub.id}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '0.5rem 0',
                              borderBottom: '1px solid var(--color-border, #e5e7eb)',
                            }}
                          >
                            <span>
                              {sub.deviceName || 'Dispositivo desconocido'}
                              {isCurrentDevice && (
                                <span className="text-success" style={{ marginLeft: '0.5rem', fontSize: '0.85em' }}>
                                  (este)
                                </span>
                              )}
                              <br />
                              <span className="text-muted" style={{ fontSize: '0.8em' }}>
                                {new Date(sub.createdAt).toLocaleDateString()}
                              </span>
                            </span>
                            {!isCurrentDevice && (
                              <button
                                onClick={() => handleRemoveDevice(sub.id)}
                                className="btn btn-ghost btn-sm"
                                disabled={isLoading}
                                style={{ color: 'var(--color-danger, #ef4444)' }}
                              >
                                Eliminar
                              </button>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}

                <p className="text-muted text-sm" style={{ marginTop: '1rem' }}>
                  Recibirás recordatorios de medicamentos, citas veterinarias y vacunas en todos los dispositivos registrados.
                </p>
              </div>
            </StatusCard.Content>
          </StatusCard>

          <StatusCard status="neutral">
            <StatusCard.Header>
              <StatusCard.Title>Sesión</StatusCard.Title>
            </StatusCard.Header>
            <StatusCard.Content collapsible={false}>
              <button onClick={logout} className="btn btn-danger">
                Cerrar sesión
              </button>
            </StatusCard.Content>
          </StatusCard>
        </div>
      </div>
    </ResponsiveLayout>
  )
}
